/**
 * Bulk export helpers for Prep and Recipe modules.
 *
 * Doctrine: exports MUST round-trip with the bulk import format.
 *   - Header column order matches the upload template exactly.
 *   - One row per ingredient line; multiple rows with the same prep_name /
 *     recipe_name represent one prep / recipe.
 *   - Recipes: PREP lines export `ingredient_id = prep.acts_as_ingredient_id`
 *     (NOT prep_id), `ingredient_name = prep.prep_name`. Notes default to
 *     "Prep used as ingredient" if no notes were stored.
 *   - Sort: preps/recipes alphabetical by name; within each, ingredients
 *     alphabetical by their export name.
 *   - No internal fields (org_id, status, created_by, etc.) are emitted.
 *   - No mutations.
 */

import * as XLSX from 'xlsx';

// Header order must match the upload templates.
// `last_updated_at` is appended (last column) — additive, backward-compatible:
// imports without this column behave like today.
export const PREP_EXPORT_HEADERS = [
  'prep_name', 'expected_yield', 'yield_unit',
  'ingredient_name', 'ingredient_id', 'quantity', 'unit',
  'wastage_percent', 'notes', 'last_updated_at',
];

export const RECIPE_EXPORT_HEADERS = [
  'recipe_name', 'recipe_type', 'category', 'portion_size', 'portion_unit',
  'yield_quantity', 'yield_unit',
  'ingredient_name', 'ingredient_id', 'quantity', 'unit',
  'wastage_percent', 'notes', 'last_updated_at',
];

const _ts = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
};

// CSV cell escape: wrap in quotes if value contains comma/quote/newline.
const _csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const _toCsv = (headers, rows) => {
  const lines = [headers.join(',')];
  rows.forEach((r) => {
    lines.push(headers.map((h) => _csvCell(r[h])).join(','));
  });
  return lines.join('\n');
};

const _downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const _downloadCsv = (rows, headers, filenameStem) => {
  const csv = _toCsv(headers, rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  _downloadBlob(blob, `${filenameStem}_${_ts()}.csv`);
};

const _downloadXlsx = (rows, headers, filenameStem, sheetName) => {
  const aoa = [headers, ...rows.map((r) => headers.map((h) => r[h] ?? ''))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${filenameStem}_${_ts()}.xlsx`);
};

// ---------------------------------------------------------------------------
// PREPS
// ---------------------------------------------------------------------------

/** Convert a list of preps + ingredients lookup into export rows. */
export const buildPrepExportRows = (preps, ingredientById) => {
  const sorted = [...(preps || [])].sort((a, b) =>
    (a.prep_name || '').localeCompare(b.prep_name || '', undefined, { sensitivity: 'base' })
  );
  const rows = [];
  sorted.forEach((p) => {
    const yieldUnit = p.base_unit || p.yield_unit || '';
    const lines = (p.recipe || p.lines || []).map((ln) => {
      const ingId = ln.ingredient_id || '';
      const ingDoc = ingredientById?.get?.(ingId) || ingredientById?.[ingId] || null;
      const ingName = ingDoc?.ingredient_name || ingDoc?.name || ln.ingredient_name || '';
      return {
        ingredient_name: ingName,
        ingredient_id: ingId,
        quantity: ln.qty ?? ln.quantity ?? '',
        unit: ln.base_unit || ln.unit || '',
        wastage_percent: ln.wastage_percent ?? 0,
        notes: ln.notes || '',
      };
    });
    lines.sort((a, b) =>
      (a.ingredient_name || '').localeCompare(b.ingredient_name || '', undefined, { sensitivity: 'base' })
    );
    lines.forEach((line) => {
      rows.push({
        prep_name: p.prep_name || '',
        expected_yield: p.expected_yield ?? '',
        yield_unit: yieldUnit,
        ...line,
        last_updated_at: p.updated_at || p.created_at || '',
      });
    });
  });
  return rows;
};

export const exportPrepsCsv = (preps, ingredientById) => {
  const rows = buildPrepExportRows(preps, ingredientById);
  _downloadCsv(rows, PREP_EXPORT_HEADERS, 'preps_export');
  return rows.length;
};

export const exportPrepsXlsx = (preps, ingredientById) => {
  const rows = buildPrepExportRows(preps, ingredientById);
  _downloadXlsx(rows, PREP_EXPORT_HEADERS, 'preps_export', 'Preps');
  return rows.length;
};

// ---------------------------------------------------------------------------
// RECIPES
// ---------------------------------------------------------------------------

/**
 * Convert a list of recipes into export rows.
 *
 * For PREP lines, we emit:
 *   ingredient_id   = prep.acts_as_ingredient_id
 *   ingredient_name = prep.prep_name
 *   notes           = original notes || 'Prep used as ingredient'
 *
 * @param recipes        array of recipe docs (with `lines`)
 * @param ingredientById Map<ingredient_id, doc> for display-name resolution
 * @param prepByPrepId   Map<prep_id, prep doc>
 * @param prepByActsAsId Map<acts_as_ingredient_id, prep doc>
 */
export const buildRecipeExportRows = (
  recipes,
  ingredientById,
  prepByPrepId,
  prepByActsAsId,
) => {
  const igGet = (id) => (ingredientById?.get?.(id) ?? ingredientById?.[id] ?? null);
  const ppGet = (id) => (prepByPrepId?.get?.(id) ?? prepByPrepId?.[id] ?? null);
  const paGet = (id) => (prepByActsAsId?.get?.(id) ?? prepByActsAsId?.[id] ?? null);

  const sorted = [...(recipes || [])].sort((a, b) =>
    (a.recipe_name || '').localeCompare(b.recipe_name || '', undefined, { sensitivity: 'base' })
  );

  const rows = [];
  sorted.forEach((r) => {
    const yields = r.yields || {};
    const portionSize =
      r.portion_size ?? yields.portion_size ?? '';
    const portionUnit =
      r.portion_unit ?? yields.portion_unit ?? '';
    const yieldQty =
      r.yield_quantity ?? yields.yield_quantity ?? yields.qty ?? '';
    const yieldUnit =
      r.yield_unit ?? yields.yield_unit ?? yields.unit ?? '';

    const lines = (r.lines || []).map((ln) => {
      const isPrep =
        ln.line_type === 'PREP' || !!ln.prep_id ||
        (ln.acts_as_ingredient_id && paGet(ln.acts_as_ingredient_id));
      let exportIngId = ln.ingredient_id || '';
      let exportIngName = '';
      let notes = ln.notes || '';

      if (isPrep) {
        const prep = ppGet(ln.prep_id) || paGet(ln.acts_as_ingredient_id || ln.ingredient_id) || null;
        if (prep) {
          exportIngId = prep.acts_as_ingredient_id || exportIngId;
          exportIngName = prep.prep_name || '';
        } else {
          // Couldn't resolve prep doc — fall back to whatever line carries.
          exportIngName = ln.ingredient_name || ln.prep_name || '';
        }
        if (!notes) notes = 'Prep used as ingredient';
      } else {
        const ingDoc = igGet(ln.ingredient_id);
        exportIngName = ingDoc?.ingredient_name || ingDoc?.name || ln.ingredient_name || '';
      }

      return {
        ingredient_name: exportIngName,
        ingredient_id: exportIngId,
        quantity: ln.qty ?? ln.quantity ?? '',
        unit: ln.base_unit || ln.unit || '',
        wastage_percent: ln.wastage_percent ?? 0,
        notes,
      };
    });

    lines.sort((a, b) =>
      (a.ingredient_name || '').localeCompare(b.ingredient_name || '', undefined, { sensitivity: 'base' })
    );

    lines.forEach((line) => {
      rows.push({
        recipe_name: r.recipe_name || '',
        recipe_type: r.recipe_type || '',
        category: r.category || '',
        portion_size: portionSize,
        portion_unit: portionUnit,
        yield_quantity: yieldQty,
        yield_unit: yieldUnit,
        ...line,
        last_updated_at: r.updated_at || r.created_at || '',
      });
    });
  });
  return rows;
};

export const exportRecipesCsv = (recipes, ingredientById, prepByPrepId, prepByActsAsId) => {
  const rows = buildRecipeExportRows(recipes, ingredientById, prepByPrepId, prepByActsAsId);
  _downloadCsv(rows, RECIPE_EXPORT_HEADERS, 'recipes_export');
  return rows.length;
};

export const exportRecipesXlsx = (recipes, ingredientById, prepByPrepId, prepByActsAsId) => {
  const rows = buildRecipeExportRows(recipes, ingredientById, prepByPrepId, prepByActsAsId);
  _downloadXlsx(rows, RECIPE_EXPORT_HEADERS, 'recipes_export', 'Recipes');
  return rows.length;
};
